import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { driver, type DriveStep } from 'driver.js';
import apiClient from '../clients/apiClient';
import DashboardLayout from '../components/layouts/DashboardLayout';
import Spinner from '../components/ui/Spinner';
import Toast from '../components/ui/Toast';
import ProfileSlideOver from '../components/ui/ProfileSlideOver';
import SendLoader from '../components/ui/SendLoader';
import FollowUpPreviewModal from '../components/ui/FollowUpPreviewModal';
import Pagination from '../components/ui/Pagination';
import { useAuth } from '../hooks/useAuth';
import { usePipeline } from '../hooks/usePipeline';
import { useWebhook } from '../hooks/useWebhook';
import { useAutomationQueue } from '../hooks/useAutomationQueue';
import type { PendingApplication } from '../hooks/useAutomationQueue';
import { useFollowUpQueue } from '../hooks/useFollowUpQueue';
import type { PendingFollowUp } from '../hooks/useFollowUpQueue';
// Referral queue temporarily disabled on staging (2026-09-26) — see ReferralQueuePanel below.
// import { useReferralQueue } from '../hooks/useReferralQueue';
// import type { PendingReferral } from '../hooks/useReferralQueue';
import type { Application, Outreach } from '../data/mock';

type OutreachStatus = Outreach['status'];

// ── Per-recruiter status badge ─────────────────────────────────────────────────

function RecruiterBadge({ status }: { status: OutreachStatus }) {
  if (status === 'replied') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-g text-white">Replied ✓</span>;
  }
  if (status === 'sent') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-g text-white">Sent</span>;
  }
  if (status === 'bounced') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-600">Bounced</span>;
  }
  if (status === 'finding') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium text-hint animate-pulse-soft" style={{ backgroundColor: '#F4F5F1' }}>Finding…</span>;
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-g-light text-g-text border border-g-border">
      Draft
    </span>
  );
}

// ── Per-application status chip (top-right of row) ────────────────────────────

function AppStatusChip({ app }: { app: Application }) {
  if (app.status === 'finding_recruiters') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-hint animate-pulse-soft" style={{ backgroundColor: '#F4F5F1' }}>
        Finding…
      </span>
    );
  }
  if (app.status === 'completed') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-g text-white">
        Complete
      </span>
    );
  }
  return (
    <span className="text-xs text-muted">
      <span className="font-medium text-g">{app.sent_count} sent</span>
      {app.replied_count > 0 && (
        <> · <span className="font-medium text-g">{app.replied_count} replied</span></>
      )}
    </span>
  );
}

// ── Send confirmation modal ───────────────────────────────────────────────────

interface ConfirmSendProps {
  company: string;
  role: string;
  maxOutreach: number;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmSendModal({ company, role, maxOutreach, onConfirm, onCancel }: ConfirmSendProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity" onClick={onCancel} />

      {/* Modal card */}
      <div className="relative bg-white rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl border border-gray-100 z-10">
        {/* Header: green paper plane badge + title & red warning */}
        <div className="flex items-start gap-4 mb-5">
          <div className="w-14 h-14 rounded-2xl bg-[#ecfdf5] flex items-center justify-center flex-shrink-0">
            <svg className="w-7 h-7 text-[#059669]" viewBox="0 0 28 28" fill="none">
              <path d="M4 17.5H8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              <path d="M6 21H10.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              <path d="M25 3L3 11.5L12 15.5L16 24.5L25 3Z" fill="currentColor" />
              <path d="M12 15.5L25 3" stroke="#ecfdf5" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <h3 className="text-[22px] font-bold text-gray-900 leading-tight">Send emails directly?</h3>
            <p className="text-sm font-semibold text-[#dc2626] mt-1">This cannot be undone once confirmed.</p>
          </div>
        </div>

        {/* Grouped detail card */}
        <div className="bg-[#f8fafc] rounded-2xl border border-gray-100 px-4 py-1 divide-y divide-gray-100 mb-4">
          <div className="flex items-center gap-3 py-3">
            <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="text-sm font-normal text-gray-500 w-24 flex-shrink-0">Company</span>
            <span className="text-sm font-bold text-gray-900 truncate">{company}</span>
          </div>
          <div className="flex items-center gap-3 py-3">
            <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
              <rect x="3" y="7" width="18" height="13" rx="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3 12h18" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-sm font-normal text-gray-500 w-24 flex-shrink-0">Role</span>
            <span className="text-sm font-bold text-gray-900 truncate">{role || 'Not specified'}</span>
          </div>
          <div className="flex items-center gap-3 py-3">
            <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
              <rect x="3" y="5" width="18" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3 7l9 6 9-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-sm font-normal text-gray-500 w-24 flex-shrink-0">Max emails</span>
            <span className="text-sm font-bold text-gray-900">{maxOutreach}</span>
          </div>
        </div>

        {/* Info banner */}
        <div className="bg-[#f0fdf4] rounded-2xl p-4 flex items-start gap-3 mb-6">
          <svg className="w-5 h-5 text-[#059669] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4m0-4h.01" />
          </svg>
          <p className="text-xs sm:text-[13px] leading-relaxed text-gray-600 font-normal">
            Up to {maxOutreach} emails will be sent from your Gmail, spaced out gradually over the next several
            minutes to keep your account safe from spam flags — not all at once.
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3 px-4 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-semibold text-sm transition-colors text-center shadow-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 py-3 px-4 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2 shadow-sm"
          >
            <span>Confirm &amp; Send</span>
            <svg className="w-4 h-4 fill-current ml-0.5" viewBox="0 0 24 24">
              <path d="M21.7 2.3a1 1 0 0 0-1-.2l-18 6a1 1 0 0 0-.1 1.8l6.6 3.3 3.3 6.6a1 1 0 0 0 1.8-.1l6-18a1 1 0 0 0-.6-1.4z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Queue Toggle Confirmation Modal ───────────────────────────────────────────

interface QueueToggleModalProps {
  type: 'automation' | 'followup';
  currentState: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

function QueueToggleModal({ type, currentState, onConfirm, onCancel, loading }: QueueToggleModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const isEnabling = !currentState;
  const isAutomation = type === 'automation';

  const title = isAutomation
    ? isEnabling ? 'Enable Automation Queue?' : 'Pause Automation Queue?'
    : isEnabling ? 'Enable Follow-up Queue?' : 'Pause Follow-up Queue?';

  const subtitle = isAutomation
    ? isEnabling
      ? 'Automatically watch Gmail for new application emails.'
      : "New applications won't be automatically detected."
    : isEnabling
      ? 'Queue follow-ups for non-responsive recruiters.'
      : 'Automated follow-up suggestions will be paused.';

  const info = isAutomation
    ? isEnabling
      ? 'When active, EextraMile detects confirmation emails from companies you apply to, extracts role details, and queues recruiter drafts for your review.'
      : 'EextraMile will stop watching your Gmail for application confirmations. Pending applications already in your queue will remain safe.'
    : isEnabling
      ? 'EextraMile will automatically identify outreach emails with no recruiter response after 4 days and queue personalized follow-up drafts for quick review.'
      : "Recruiters who haven't replied after 4 days will no longer be queued for automated follow-up reminders. You can resume anytime without losing past records.";

  const confirmLabel = isAutomation
    ? isEnabling ? 'Enable Automation' : 'Pause Automation'
    : isEnabling ? 'Enable Follow-ups' : 'Pause Follow-ups';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity" onClick={onCancel} />

      {/* Modal card */}
      <div className="relative bg-white rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl border border-gray-100 z-10 animate-fade-in">
        {/* Header */}
        <div className="flex items-start gap-4 mb-5">
          <div
            className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
              isEnabling ? 'bg-[#ecfdf5] text-[#059669]' : 'bg-amber-50 text-amber-600'
            }`}
          >
            {isAutomation ? (
              isEnabling ? (
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
              ) : (
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="10" y1="15" x2="10" y2="9" />
                  <line x1="14" y1="15" x2="14" y2="9" />
                </svg>
              )
            ) : isEnabling ? (
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
                <path d="M12 7v5l3 3" />
              </svg>
            ) : (
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="10" y1="15" x2="10" y2="9" />
                <line x1="14" y1="15" x2="14" y2="9" />
              </svg>
            )}
          </div>
          <div>
            <h3 className="text-[22px] font-bold text-gray-900 leading-tight">{title}</h3>
            <p className={`text-sm font-medium mt-1 ${isEnabling ? 'text-gray-500' : 'text-amber-700'}`}>
              {subtitle}
            </p>
          </div>
        </div>

        {/* Info banner */}
        <div
          className={`rounded-2xl p-4 flex items-start gap-3 mb-6 ${
            isEnabling ? 'bg-[#f0fdf4]' : 'bg-amber-50/70'
          }`}
        >
          <svg
            className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isEnabling ? 'text-[#059669]' : 'text-amber-600'}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4m0-4h.01" />
          </svg>
          <p className="text-xs sm:text-[13px] leading-relaxed text-gray-600 font-normal">
            {info}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-3 px-4 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-semibold text-sm transition-colors text-center shadow-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 py-3 px-4 rounded-xl text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2 shadow-sm disabled:opacity-70 ${
              isEnabling
                ? 'bg-[#059669] hover:bg-[#047857]'
                : 'bg-amber-600 hover:bg-amber-700'
            }`}
          >
            {loading ? <Spinner /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Automation queue panel (left column) ──────────────────────────────────────

interface AutomationQueuePanelProps {
  triggerOn: boolean;
  onToggle: () => void;
  toggleLoading: boolean;
}

function AutomationQueuePanel({ triggerOn, onToggle, toggleLoading }: AutomationQueuePanelProps) {
  const { data: pending = [], isLoading, approve, ignore, loadingId } = useAutomationQueue();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canDirectSend = user?.plan === 'growth' || user?.plan === 'pro';
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [roleEdits, setRoleEdits] = useState<Record<string, string>>({});
  const [queueMaxOutreach, setQueueMaxOutreach] = useState<3 | 5 | 10>(10);
  const [queueAttachResume, setQueueAttachResume] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, { company: string; role: string }>>({});

  const isUnknownRole = (role: string | null) => !role || role === 'Unknown Role';

  const handleApprove = async (app: PendingApplication) => {
    const roleOverride = roleEdits[app.id]?.trim();
    if (isUnknownRole(app.role) && !roleOverride) {
      setToast({ message: 'Please enter the job role before creating drafts.', type: 'error' });
      return;
    }
    try {
      await approve(app.id, roleOverride || undefined, queueMaxOutreach, 'draft', queueAttachResume);
      setToast({ message: `Finding recruiters for ${app.company ?? 'this company'}…`, type: 'success' });
    } catch {
      setToast({ message: 'Insufficient credits or Gmail not connected.', type: 'error' });
    }
  };

  const handleIgnore = async (app: PendingApplication) => {
    await ignore(app.id);
  };

  const handleStartEdit = (app: PendingApplication) => {
    setEditingId(app.id);
    setEditValues(v => ({ ...v, [app.id]: { company: app.company ?? '', role: app.role ?? '' } }));
  };

  const [confirmSendApp, setConfirmSendApp] = useState<PendingApplication | null>(null);

  const handleSaveEdit = async (app: PendingApplication) => {
    const vals = editValues[app.id];
    if (!vals) return;
    try {
      await apiClient.patch(`/api/pipeline/${app.id}/edit`, {
        company: vals.company !== app.company ? vals.company : undefined,
        role: vals.role !== app.role ? vals.role : undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ['automation-pending'] });
      setEditingId(null);
    } catch {
      setToast({ message: 'Failed to save. Please try again.', type: 'error' });
    }
  };

  return (
    <>
      <div className="bg-card border border-border rounded-card overflow-hidden">
        {/* Header */}
        <div id="tour-automation-queue-header" className="px-4 py-3.5 border-b border-border flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[1.2px] text-hint font-semibold">Automation Queue</p>
            <p className="text-xs text-muted mt-0.5">
              {triggerOn ? 'Watching Gmail' : 'Paused'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {pending.length > 0 && (
              <span className="inline-flex items-center justify-center h-5 px-2 rounded-full bg-g text-white text-xs font-semibold">
                {pending.length}
              </span>
            )}
            <button
              onClick={onToggle}
              disabled={toggleLoading}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${toggleLoading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              style={{ backgroundColor: triggerOn ? 'var(--g)' : 'var(--hint)' }}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200 ${triggerOn ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>

        {/* Recruiter count selector + resume toggle — visible when there are pending items */}
        {!isLoading && pending.length > 0 && (
          <div className="px-4 py-2.5 border-b border-border flex items-center gap-3">
            <span
              className="text-[11px] uppercase tracking-[1.2px] text-hint font-semibold flex-shrink-0"
              title="Maximum — smaller companies may have fewer recruiters to find"
            >
              Drafts
            </span>
            <div className="flex gap-1.5">
              {([3, 5, 10] as const).map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setQueueMaxOutreach(n)}
                  className="px-2.5 py-1 rounded text-xs font-semibold transition-colors border"
                  style={{
                    backgroundColor: queueMaxOutreach === n ? 'var(--g)' : 'transparent',
                    color: queueMaxOutreach === n ? 'white' : 'var(--hint)',
                    borderColor: queueMaxOutreach === n ? 'var(--g)' : 'var(--border)',
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
            {canDirectSend && (
              <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                <span
                  className="text-[11px] uppercase tracking-[1.2px] font-semibold"
                  style={{ color: user?.resume_pdf_path ? 'var(--hint)' : 'var(--border)' }}
                  title={!user?.resume_pdf_path ? 'Upload a resume in Settings first' : undefined}
                >
                  Resume
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={queueAttachResume}
                  disabled={!user?.resume_pdf_path}
                  onClick={() => setQueueAttachResume(v => !v)}
                  className="relative inline-flex h-4 w-7 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ backgroundColor: queueAttachResume && user?.resume_pdf_path ? 'var(--g)' : 'var(--border)' }}
                  title={!user?.resume_pdf_path ? 'Upload a resume in Settings first' : 'Attach resume to emails'}
                >
                  <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform duration-200 ${queueAttachResume && user?.resume_pdf_path ? 'translate-x-3' : 'translate-x-0'}`} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Body */}
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : pending.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
            <div className="h-9 w-9 rounded-full bg-bg flex items-center justify-center mb-2.5">
              <svg className="h-4.5 w-4.5 text-hint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-ink">All clear</p>
            <p className="text-xs text-muted mt-0.5">New job applications will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {pending.map((app) => {
              const isActing = loadingId === app.id;
              const needsRole = isUnknownRole(app.role);
              const isEditing = editingId === app.id;
              return (
                <div key={app.id} className="px-4 py-3.5">
                  {isEditing ? (
                    <>
                      {/* Edit inputs */}
                      <div className="flex flex-col gap-1.5 mb-3">
                        <input
                          autoFocus
                          value={editValues[app.id]?.company ?? ''}
                          onChange={e => setEditValues(v => ({ ...v, [app.id]: { ...v[app.id], company: e.target.value } }))}
                          placeholder="Company"
                          className="w-full rounded-input px-2.5 py-1.5 text-sm font-semibold text-ink bg-bg focus:outline-none"
                          style={{ border: '1px solid var(--g)' }}
                        />
                        <input
                          value={editValues[app.id]?.role ?? ''}
                          onChange={e => setEditValues(v => ({ ...v, [app.id]: { ...v[app.id], role: e.target.value } }))}
                          placeholder="Role"
                          className="w-full rounded-input px-2.5 py-1.5 text-xs text-ink bg-bg focus:outline-none"
                          style={{ border: '1px solid var(--border)' }}
                          onFocus={e => (e.target.style.borderColor = 'var(--g)')}
                          onBlur={e => (e.target.style.borderColor = 'var(--border)')}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSaveEdit(app)}
                          className="flex-1 flex items-center justify-center py-1.5 px-3 rounded-input text-xs font-semibold text-white transition-colors"
                          style={{ backgroundColor: 'var(--g)' }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="py-1.5 px-3 rounded-input text-xs font-medium text-muted border border-border hover:text-ink transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Company + role */}
                      <div className="flex items-center gap-2.5 mb-3">
                        <div
                          className="h-8 w-8 flex-shrink-0 flex items-center justify-center text-sm font-serif italic text-muted bg-bg border border-border"
                          style={{ borderRadius: '8px' }}
                        >
                          {(app.company ?? '?')[0]?.toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-ink truncate">{app.company ?? 'Unknown Company'}</p>
                          {!needsRole && (
                            <p className="text-xs text-muted truncate">{app.role}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleStartEdit(app)}
                          className="flex-shrink-0 p-1 rounded text-hint hover:text-ink transition-colors"
                          title="Edit company or role"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>

                      {/* Role input — shown when Claude couldn't extract the role */}
                      {needsRole && (
                        <input
                          type="text"
                          placeholder="What role did you apply for?"
                          value={roleEdits[app.id] ?? ''}
                          onChange={e => setRoleEdits(r => ({ ...r, [app.id]: e.target.value }))}
                          className="w-full rounded-input px-3 py-2 text-xs text-ink bg-bg mb-3 focus:outline-none"
                          style={{ border: '1px solid var(--border)' }}
                          onFocus={e => (e.target.style.borderColor = 'var(--g)')}
                          onBlur={e => (e.target.style.borderColor = 'var(--border)')}
                        />
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleApprove(app)}
                          disabled={isActing}
                          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-input text-xs font-semibold text-white disabled:opacity-60 transition-colors"
                          style={{ backgroundColor: 'var(--g)' }}
                          onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = 'var(--g-text)'; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'var(--g)'; }}
                        >
                          {isActing ? <Spinner /> : 'Create Drafts'}
                        </button>
                        {canDirectSend ? (
                          <button
                            onClick={() => setConfirmSendApp(app)}
                            disabled={isActing}
                            className="py-1.5 px-2 rounded-input text-xs font-semibold disabled:opacity-60 transition-colors border border-amber-300 text-amber-700 hover:bg-amber-50"
                          >
                            Send Now
                          </button>
                        ) : (
                          <a
                            href="/settings"
                            className="py-1.5 px-2 rounded-input text-xs font-semibold border border-border text-hint hover:text-muted transition-colors"
                            title="Upgrade to Growth to unlock direct send"
                          >
                            🔒 Send
                          </a>
                        )}
                        <button
                          onClick={() => handleIgnore(app)}
                          disabled={isActing}
                          className="py-1.5 px-2 rounded-input text-xs font-medium text-muted hover:text-ink disabled:opacity-60 transition-colors border border-border hover:border-ink"
                        >
                          Ignore
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {confirmSendApp && (
        <ConfirmSendModal
          company={confirmSendApp.company ?? ''}
          role={roleEdits[confirmSendApp.id]?.trim() || confirmSendApp.role || ''}
          maxOutreach={queueMaxOutreach}
          onCancel={() => setConfirmSendApp(null)}
          onConfirm={async () => {
            const app = confirmSendApp;
            setConfirmSendApp(null);
            const roleOverride = roleEdits[app.id]?.trim();
            if (isUnknownRole(app.role) && !roleOverride) {
              setToast({ message: 'Please enter the job role before sending.', type: 'error' });
              return;
            }
            try {
              await approve(app.id, roleOverride || undefined, queueMaxOutreach, 'send', queueAttachResume);
              setToast({ message: `Sending emails to recruiters at ${app.company ?? 'this company'}…`, type: 'success' });
            } catch {
              setToast({ message: 'Insufficient credits or Gmail not connected.', type: 'error' });
            }
          }}
        />
      )}
    </>
  );
}

// ── Follow-up queue panel ──────────────────────────────────────────────────────

interface FollowUpQueuePanelProps {
  followUpOn: boolean;
  onToggle: () => void;
  canFollowUp: boolean;
  onPreview?: (outreachId: string) => void;
}

function FollowUpQueuePanel({ followUpOn, onToggle, canFollowUp, onPreview }: FollowUpQueuePanelProps) {
  const { data: pending = [], isLoading, approve, skip, skipAll, loadingId, loadingCompany } = useFollowUpQueue();
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const daysAgo = (sentAt: string | null) => {
    if (!sentAt) return null;
    const diff = Math.floor((Date.now() - new Date(sentAt).getTime()) / 86_400_000);
    return diff === 1 ? '1 day ago' : `${diff} days ago`;
  };

  // Group by company
  const grouped = useMemo(() => {
    const map = new Map<string, PendingFollowUp[]>();
    for (const o of pending) {
      const key = o.company || 'Unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(o);
    }
    return Array.from(map.entries());
  }, [pending]);

  const toggleCollapse = (company: string) =>
    setCollapsed(prev => ({ ...prev, [company]: !prev[company] }));

  const handleApprove = async (o: PendingFollowUp) => {
    try {
      await approve(o.id);
      setToast({ message: `Follow-up sent to ${o.recruiter_name} at ${o.company}`, type: 'success' });
    } catch {
      setToast({ message: 'Failed to send follow-up. Try again.', type: 'error' });
    }
  };

  const handleSkipAll = async (company: string, ids: string[]) => {
    await skipAll(company, ids);
  };

  return (
    <>
      <div className="bg-card border border-border rounded-card overflow-hidden">
        {/* Header */}
        <div id="tour-followup-queue-header" className="px-4 py-3.5 border-b border-border flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[1.2px] text-hint font-semibold">Follow-up Queue</p>
            <p className="text-xs text-muted mt-0.5">
              {!canFollowUp ? <a href="/pricing" className="text-g hover:underline">Upgrade to unlock</a>
                : followUpOn ? 'Auto after 4 days' : 'Paused'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {pending.length > 0 && (
              <span className="inline-flex items-center justify-center h-5 px-2 rounded-full bg-amber-500 text-white text-xs font-semibold">
                {pending.length}
              </span>
            )}
            {canFollowUp ? (
              <button
                onClick={onToggle}
                className="relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 cursor-pointer"
                style={{ backgroundColor: followUpOn ? 'var(--g)' : 'var(--hint)' }}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200 ${followUpOn ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            ) : (
              <span className="text-sm" title="Upgrade to Growth to unlock">🔒</span>
            )}
          </div>
        </div>

        {/* Body */}
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : grouped.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
            <div className="h-9 w-9 rounded-full bg-bg flex items-center justify-center mb-2.5">
              <svg className="h-4.5 w-4.5 text-hint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-ink">All caught up</p>
            <p className="text-xs text-muted mt-0.5">Follow-ups will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {grouped.map(([company, items]) => {
              const isOpen = !collapsed[company];
              const ids = items.map(o => o.id);
              const isSkippingAll = loadingCompany === company;

              return (
                <div key={company}>
                  {/* Company header row */}
                  <div className="px-4 py-3 flex items-center gap-2 bg-sand">
                    <button
                      onClick={() => toggleCollapse(company)}
                      className="flex items-center gap-2 flex-1 min-w-0"
                    >
                      <div
                        className="h-7 w-7 flex-shrink-0 flex items-center justify-center text-xs font-serif italic text-muted bg-card border border-border"
                        style={{ borderRadius: '6px' }}
                      >
                        {company[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-xs font-semibold text-ink truncate">{company}</p>
                        <p className="text-[10px] text-muted">{items.length} {items.length === 1 ? 'person' : 'people'}</p>
                      </div>
                      <svg
                        className={`h-3.5 w-3.5 text-hint flex-shrink-0 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleSkipAll(company, ids)}
                      disabled={isSkippingAll}
                      className="flex-shrink-0 text-[10px] font-medium text-muted hover:text-ink border border-border rounded px-2 py-1 hover:bg-card disabled:opacity-50 transition-colors"
                    >
                      {isSkippingAll ? <Spinner /> : 'Skip all'}
                    </button>
                  </div>

                  {/* Individual rows */}
                  {isOpen && (
                    <div className="divide-y divide-border">
                      {items.map((o) => {
                        const isActing = loadingId === o.id;
                        return (
                          <div key={o.id} className="px-4 py-3 pl-6">
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-ink truncate">{o.recruiter_name}</p>
                                <p className="text-[10px] text-muted truncate">{o.role}</p>
                              </div>
                              <div className="flex items-center gap-1.5 mb-3">
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
                                  #{o.follow_up_number}
                                </span>
                              </div>
                            </div>
                            {daysAgo(o.sent_at) && (
                              <p className="text-[10px] text-muted mb-2">sent {daysAgo(o.sent_at)}</p>
                            )}
                            <div className="flex items-center gap-2">
                              {onPreview && (
                                <button
                                  onClick={() => onPreview(o.id)}
                                  disabled={isActing}
                                  className="py-1.5 px-2.5 rounded-input text-xs font-semibold text-ink bg-gray-100 hover:bg-gray-200 border border-border disabled:opacity-60 transition-colors"
                                  title="Preview email before sending"
                                >
                                  Preview
                                </button>
                              )}
                              <button
                                onClick={() => handleApprove(o)}
                                disabled={isActing}
                                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-input text-xs font-semibold text-white disabled:opacity-60 transition-colors"
                                style={{ backgroundColor: 'var(--g)' }}
                                onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = 'var(--g-text)'; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'var(--g)'; }}
                              >
                                {isActing ? <Spinner /> : 'Send'}
                              </button>
                              <button
                                onClick={() => skip(o.id)}
                                disabled={isActing}
                                className="py-1.5 px-2.5 rounded-input text-xs font-medium text-muted hover:text-ink hover:bg-sand disabled:opacity-60 transition-colors border border-border"
                              >
                                Skip
                              </button>
                            </div>
                          </div>
                  );
                      })}
                </div>
              )
            }
                </div>
        );
            })}
      </div>
        )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div >
    </>
  );
}

// ── Referral queue panel ──────────────────────────────────────────────────────
// Disabled on staging as of 2026-09-26 — not deleted, revisit later. Also
// commented: the useReferralQueue/PendingReferral import above, the
// referralPendingData badge count, and both <ReferralQueuePanel /> render
// call sites further down this file.
// function ReferralQueuePanel() {
//   const { data: pending = [], isLoading, approve, dismiss, loadingId } = useReferralQueue();
//   const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
// 
//   const handleApprove = async (r: PendingReferral) => {
//     try {
//       await approve(r.id);
//       setToast({ message: `Draft created for ${r.referred_name} at ${r.company}`, type: 'success' });
//     } catch {
//       setToast({ message: 'Failed to create draft. Try again.', type: 'error' });
//     }
//   };
// 
//   const handleDismiss = async (r: PendingReferral) => {
//     try {
//       await dismiss(r.id);
//     } catch {
//       setToast({ message: 'Failed to dismiss referral.', type: 'error' });
//     }
//   };
// 
//   return (
//     <div className="w-full lg:w-72 flex-shrink-0">
//       <div className="bg-card border border-border rounded-card overflow-hidden">
//         {/* Header */}
//         <div className="px-4 py-3.5 border-b border-border flex items-center justify-between">
//           <div>
//             <p className="text-[11px] uppercase tracking-[1.2px] text-hint font-semibold">Referral Queue</p>
//             <p className="text-xs text-muted mt-0.5">Contacts referred by recruiters</p>
//           </div>
//           {pending.length > 0 && (
//             <span className="inline-flex items-center justify-center h-5 px-2 rounded-full bg-blue-500 text-white text-xs font-semibold">
//               {pending.length}
//             </span>
//           )}
//         </div>
// 
//         {/* Body */}
//         {isLoading ? (
//           <div className="flex justify-center py-8"><Spinner /></div>
//         ) : pending.length === 0 ? (
//           <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
//             <div className="h-9 w-9 rounded-full bg-bg flex items-center justify-center mb-2.5">
//               <svg className="h-4.5 w-4.5 text-hint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
//               </svg>
//             </div>
//             <p className="text-sm font-medium text-ink">No referrals yet</p>
//             <p className="text-xs text-muted mt-0.5">Referred contacts will appear here</p>
//           </div>
//         ) : (
//           <div className="divide-y divide-border">
//             {pending.map((r) => {
//               const isActing = loadingId === r.id;
//               return (
//                 <div key={r.id} className="px-4 py-3.5">
//                   <div className="flex items-start gap-2.5 mb-2">
//                     <div
//                       className="h-8 w-8 flex-shrink-0 flex items-center justify-center text-sm font-serif italic text-muted bg-bg border border-border"
//                       style={{ borderRadius: '8px' }}
//                     >
//                       {(r.referred_name || '?')[0]?.toUpperCase()}
//                     </div>
//                     <div className="min-w-0 flex-1">
//                       <p className="text-sm font-semibold text-ink truncate">{r.referred_name}</p>
//                       <p className="text-[10px] text-muted truncate font-mono">{r.referred_email}</p>
//                     </div>
//                   </div>
//                   {r.referred_by_name && (
//                     <p className="text-[10px] text-hint mb-2.5">
//                       Referred by <span className="text-muted font-medium">{r.referred_by_name}</span> · {r.company}
//                     </p>
//                   )}
//                   <div className="flex items-center gap-1.5">
//                     <button
//                       onClick={() => handleApprove(r)}
//                       disabled={isActing}
//                       className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-input text-xs font-semibold text-white disabled:opacity-60 transition-colors"
//                       style={{ backgroundColor: 'var(--g)' }}
//                       onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = 'var(--g-text)'; }}
//                       onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'var(--g)'; }}
//                     >
//                       {isActing ? <Spinner /> : 'Draft Email'}
//                     </button>
//                     <button
//                       onClick={() => handleDismiss(r)}
//                       disabled={isActing}
//                       className="py-1.5 px-2 rounded-input text-xs font-medium text-muted hover:text-ink disabled:opacity-60 transition-colors border border-border hover:border-ink"
//                     >
//                       Dismiss
//                     </button>
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//         )}
//         {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
//       </div>
//     </div>
//   );
// }

// ── Single application row ─────────────────────────────────────────────────────

function ApplicationRow({ app }: { app: Application }) {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const isFinding = app.status === 'finding_recruiters';
  const visibleOutreaches = showAll ? app.outreaches : app.outreaches.slice(0, 5);
  const remaining = app.outreaches.length - 5;
  const remainingSent = app.outreaches.slice(5).filter(o => o.status === 'sent' || o.status === 'replied').length;
  const remainingDrafts = remaining - remainingSent;

  return (
    <div className="border-b border-border last:border-0">
      {/* Row header */}
      <div className="px-5 py-3.5 flex items-center justify-between gap-4 hover:bg-bg transition-colors">
        <button
          className="flex items-start gap-3 min-w-0 flex-1 text-left py-0.5"
          onClick={() => setExpanded(v => !v)}
        >
          {/* Company icon */}
          <div
            className="h-[36px] w-[36px] flex-shrink-0 flex items-center justify-center text-base font-serif italic text-muted bg-bg border border-border mt-0.5"
            style={{ borderRadius: '10px' }}
          >
            {app.company[0]?.toUpperCase() ?? '?'}
          </div>

          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm sm:text-base text-ink leading-snug truncate">
              {app.company}
            </p>
            <p className="text-xs sm:text-sm font-medium text-muted leading-snug mt-0.5 truncate">
              {app.role}
            </p>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <span className="text-xs font-medium text-hint">{app.date}</span>
              {app.outreach_count > 0 && (
                <>
                  <span className="text-xs font-medium text-hint">·</span>
                  <span className="text-xs font-medium text-hint">{app.outreach_count} outreaches</span>
                </>
              )}
              {app.sent_count > 0 && (
                <>
                  <span className="text-xs font-medium text-hint">·</span>
                  <span className="text-xs font-medium text-hint">{app.sent_count} sent</span>
                </>
              )}
            </div>
          </div>
        </button>

        <div className="flex items-center gap-2 flex-shrink-0">
          <AppStatusChip app={app} />
          <button onClick={() => setExpanded(v => !v)}>
            <div
              className={`h-6 w-6 rounded-full flex items-center justify-center border transition-all ${expanded ? 'bg-g border-g text-white' : 'bg-card border-border text-muted'
                }`}
            >
              <svg
                className={`h-3 w-3 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        </div>
      </div>

      {/* Expanded recruiter list */}
      {expanded && (
        <div className="pb-4 pl-3 sm:pl-16 pr-3 sm:pr-5">
          {isFinding ? (
            <div className="space-y-2 py-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-4 bg-border rounded animate-pulse" />
              ))}
              <p className="text-xs text-hint animate-pulse-soft">Searching for recruiters at {app.company}…</p>
            </div>
          ) : app.outreaches.length === 0 ? (
            <p className="text-sm text-hint py-2">No outreaches yet.</p>
          ) : (
            <div>
              {visibleOutreaches.map(o => (
                <div key={o.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="text-xs text-hint flex-shrink-0">—</span>
                    <div className="min-w-0">
                      {o.gmail_thread_id ? (
                        <a
                          href={`https://mail.google.com/mail/u/0/#all/${o.gmail_thread_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="text-sm font-semibold text-ink hover:text-g hover:underline"
                        >
                          {o.recruiter_name} ↗
                        </a>
                      ) : (
                        <span className="text-sm font-semibold text-ink">{o.recruiter_name}</span>
                      )}
                      {o.title && <span className="text-xs font-medium text-hint ml-2 hidden sm:inline">{o.title}</span>}
                    </div>
                  </div>
                  <RecruiterBadge status={o.status} />
                </div>
              ))}

              {!showAll && remaining > 0 && (
                <button
                  onClick={e => { e.stopPropagation(); setShowAll(true); }}
                  className="text-xs text-g hover:underline mt-2"
                >
                  + {remaining} more
                  {remainingSent > 0 && remainingDrafts > 0 && ` (${remainingSent} sent, ${remainingDrafts} draft)`}
                  {remainingSent > 0 && remainingDrafts === 0 && ' sent'}
                  {remainingDrafts > 0 && remainingSent === 0 && ' drafts'}
                </button>
              )}

              <div className="flex items-center gap-3 pt-3">
                <a
                  href="https://mail.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-g hover:bg-g-text text-white rounded-input text-xs font-semibold transition-colors"
                >
                  Open in Gmail ↗
                </a>
                <Link
                  to={`/dashboard/application/${app.id}`}
                  onClick={e => e.stopPropagation()}
                  className="text-xs text-g font-medium hover:underline"
                >
                  View details →
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main dashboard ─────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const canDirectSend = user?.plan === 'growth' || user?.plan === 'pro';
  const { data: applications = [], isLoading: pipelineLoading, refetch: refetchPipeline } = usePipeline();
  const { triggerOutreach, state: webhookState, errorKind, reset: resetWebhook } = useWebhook();

  const [form, setForm] = useState({ company: '', role: '', country: '', max_outreach: 10 as 3 | 5 | 10 });
  const [formSendMode, setFormSendMode] = useState<'draft' | 'send'>('draft');
  const [attachResume, setAttachResume] = useState(false);
  const [pendingDirectSend, setPendingDirectSend] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [showReconnectGmail, setShowReconnectGmail] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const APPLICATIONS_PER_PAGE = 5;
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [queueDrawerOpen, setQueueDrawerOpen] = useState(false);
  const [outreachLaunched, setOutreachLaunched] = useState(false);
  const [confirmQueueToggle, setConfirmQueueToggle] = useState<'automation' | 'followup' | null>(null);

  const hasProfile = !!(user?.resume_summary?.trim());
  const triggerOn = user?.trigger_enabled ?? false;
  const [followUpOn, setFollowUpOn] = useState(user?.follow_up_enabled ?? false);
  useEffect(() => { setFollowUpOn(user?.follow_up_enabled ?? false); }, [user]);

  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reviewFollowUpId, setReviewFollowUpId] = useState<string | null>(null);

  useEffect(() => {
    const action = searchParams.get('action');
    const outreachId = searchParams.get('outreach_id');
    if (action === 'review_follow_up' && outreachId) {
      setReviewFollowUpId(outreachId);
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      next.delete('outreach_id');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Counts for the mobile queue drawer badge — panels below fetch the same
  // React-Query-cached keys, so this doesn't add extra network requests.
  const { data: automationPendingData = [] } = useAutomationQueue();
  const { data: followUpPendingData = [] } = useFollowUpQueue();
  // const { data: referralPendingData = [] } = useReferralQueue(); // referral queue disabled on staging, see below
  const queueBadgeCount = automationPendingData.length + followUpPendingData.length; // + referralPendingData.length

  useEffect(() => {
    document.body.style.overflow = queueDrawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [queueDrawerOpen]);

  const startTour = useCallback(() => {
    const isMobile = window.innerWidth < 1024;

    const queueSteps: DriveStep[] = isMobile
      ? [
          {
            element: '#tour-automation-queue-mobile',
            popover: {
              title: 'Automation Queue',
              description: 'When you apply to a job, EextraMile detects the confirmation email from Gmail and adds it here. Review and approve to start outreach.',
              side: 'bottom',
              align: 'center',
            },
            onHighlightStarted: (_el, _step, { driver: d }) => {
              setQueueDrawerOpen(true);
              const scrollContainer = document.getElementById('tour-mobile-drawer-scroll');
              if (scrollContainer) {
                scrollContainer.scrollTop = 0;
              }
              setTimeout(() => d.refresh(), 320);
            },
          },
          {
            element: '#tour-followup-queue-mobile',
            popover: {
              title: 'Follow-up Queue',
              description: "Recruiters who haven't replied after a day appear here. Approve to send a follow-up, or skip — you stay in control.",
              side: 'top',
              align: 'center',
            },
            onHighlightStarted: (_el, _step, { driver: d }) => {
              setQueueDrawerOpen(true);
              const followUpEl = document.getElementById('tour-followup-queue-mobile');
              if (followUpEl) {
                followUpEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
              setTimeout(() => d.refresh(), 320);
            },
          },
        ]
      : [
          {
            element: '#tour-automation-queue',
            popover: {
              title: 'Automation Queue',
              description: 'When you apply to a job, EextraMile detects the confirmation email from Gmail and adds it here. Review and approve to start outreach.',
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '#tour-followup-queue',
            popover: {
              title: 'Follow-up Queue',
              description: 'Recruiters who haven\'t replied after a day appear here. Approve to send a follow-up, or skip — you stay in control.',
              side: 'left',
              align: 'start',
            },
          },
        ];

    const tour = driver({
      showProgress: true,
      smoothScroll: true,
      stagePadding: 6,
      popoverOffset: 12,
      progressText: '{{current}} of {{total}}',
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Got it!',
      popoverClass: 'eextramile-tour',
      steps: [
        {
          element: '#tour-credits',
          popover: {
            title: 'Your Credits',
            description: 'Each email sent to a recruiter costs 1 credit. Credits never expire — buy more any time.',
            side: 'bottom',
            align: 'end',
          },
        },
        ...queueSteps,
        {
          element: '#tour-stats',
          popover: {
            title: 'Your Progress',
            description: 'Track how many applications you\'ve triggered, total outreach emails, and recruiter replies — all in one place.',
            side: 'bottom',
            align: 'start',
          },
          onHighlightStarted: () => {
            if (window.innerWidth < 1024) {
              setQueueDrawerOpen(false);
            }
          },
        },
        {
          element: '#tour-manual-outreach',
          popover: {
            title: 'Manual Outreach',
            description: 'Already applied somewhere? Enter the company and role here to find recruiters and draft personalised emails instantly.',
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#tour-applications',
          popover: {
            title: 'Your Pipeline',
            description: 'Every application lives here. Expand any row to see the recruiters contacted, their email status, and links to the Gmail thread.',
            side: 'top',
            align: 'start',
          },
        },
      ],
      onDestroyStarted: () => {
        localStorage.setItem('tour_done', '1');
        setQueueDrawerOpen(false);
        tour.destroy();
      },
    });
    tour.drive();
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('tour_done')) {
      const timer = setTimeout(startTour, 800);
      return () => clearTimeout(timer);
    }
  }, [startTour]);

  // Time-based greeting
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.name?.split(' ')[0] ?? '';

  const totalOutreaches = applications.reduce((a, b) => a + b.outreach_count, 0);
  const totalReplies = applications.reduce((a, b) => a + b.replied_count, 0);

  const filteredApps = useMemo(
    () => applications.filter(a =>
      a.company.toLowerCase().includes(search.toLowerCase()) ||
      a.role.toLowerCase().includes(search.toLowerCase())
    ),
    [applications, search],
  );

  // Reset to first page when search filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(filteredApps.length / APPLICATIONS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedApps = useMemo(() => {
    const start = (safeCurrentPage - 1) * APPLICATIONS_PER_PAGE;
    return filteredApps.slice(start, start + APPLICATIONS_PER_PAGE);
  }, [filteredApps, safeCurrentPage]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.company || !form.role) {
      setToast({ message: 'Please fill in all fields', type: 'error' });
      return;
    }
    setShowReconnectGmail(false);
    if (formSendMode === 'send') {
      if (!canDirectSend) {
        window.location.href = '/pricing';
        return;
      }
      setPendingDirectSend(true);
      return;
    }
    setOutreachLaunched(false);
    await triggerOutreach({ company: form.company, role: form.role, country: form.country || undefined, max_outreach: form.max_outreach, attach_resume: attachResume });
  };

  const handleConfirmDirectSend = async () => {
    setPendingDirectSend(false);
    setOutreachLaunched(false);
    await triggerOutreach({ company: form.company, role: form.role, country: form.country || undefined, max_outreach: form.max_outreach, send_mode: 'send', attach_resume: attachResume });
  };

  const handleTriggerToggle = async () => {
    setTriggerLoading(true);
    try {
      await apiClient.post(triggerOn ? '/api/automation/disable' : '/api/automation/enable');
      await refreshUser();
    } catch {
      // silent — state unchanged on failure
    } finally {
      setTriggerLoading(false);
    }
  };

  const handleFollowUpToggle = async () => {
    const next = !followUpOn;
    setFollowUpOn(next);
    try {
      await apiClient.patch('/api/user/profile', { follow_up_enabled: next });
    } catch {
      setFollowUpOn(!next); // revert on failure
    }
  };

  const handleConfirmQueueToggle = async () => {
    const target = confirmQueueToggle;
    setConfirmQueueToggle(null);
    if (target === 'automation') {
      await handleTriggerToggle();
    } else if (target === 'followup') {
      await handleFollowUpToggle();
    }
  };

  useEffect(() => {
    if (webhookState === 'error') {
      if (errorKind === 'insufficient_credits') {
        setToast({ message: 'Not enough credits. Buy more in Settings.', type: 'error' });
      } else if (errorKind === 'gmail_not_connected') {
        setShowReconnectGmail(true);
      } else {
        setToast({ message: 'Failed to trigger outreach. Please try again.', type: 'error' });
      }
      resetWebhook();
      setOutreachLaunched(false);
    }
  }, [webhookState, errorKind, resetWebhook]);

  return (
    <>
      <DashboardLayout onEditProfile={() => setProfileOpen(true)} onStartTour={startTour}>
        {/* ── Mobile: queues live in a slide-out drawer, not stacked below Applications ── */}
        <div className="lg:hidden">
          <button
            onClick={() => setQueueDrawerOpen(true)}
            className="fixed bottom-5 right-5 z-30 flex items-center gap-2 bg-g rounded-full shadow-lg shadow-emerald-950/25 px-4 py-2.5 text-white hover:bg-emerald-600 transition-transform active:scale-95"
            aria-label="Open queues"
          >
            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="text-xs font-semibold text-white">Queues</span>
            {queueBadgeCount > 0 && (
              <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-white text-g-text text-[11px] font-bold shadow-sm">
                {queueBadgeCount}
              </span>
            )}
          </button>

          {/* Overlay */}
          <div
            className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${queueDrawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            onClick={() => setQueueDrawerOpen(false)}
          />

          {/* Drawer panel */}
          <div
            className={`fixed top-0 left-0 h-full w-[85%] max-w-sm bg-bg z-50 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${queueDrawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
          >
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-border bg-card flex-shrink-0">
              <p className="text-base font-semibold text-ink" style={{ fontFamily: 'Lora, serif' }}>Queues</p>
              <button onClick={() => setQueueDrawerOpen(false)} className="text-hint hover:text-ink transition-colors" aria-label="Close">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div id="tour-mobile-drawer-scroll" className="flex-1 overflow-y-auto p-4 space-y-4 pb-64">
              <div id="tour-automation-queue-mobile">
                <AutomationQueuePanel
                  triggerOn={triggerOn}
                  onToggle={() => setConfirmQueueToggle('automation')}
                  toggleLoading={triggerLoading}
                />
              </div>
              <div id="tour-followup-queue-mobile">
                <FollowUpQueuePanel
                  followUpOn={followUpOn}
                  onToggle={() => setConfirmQueueToggle('followup')}
                  canFollowUp={user?.plan === 'growth' || user?.plan === 'pro'}
                  onPreview={setReviewFollowUpId}
                />
              </div>
              {/* <ReferralQueuePanel /> — disabled on staging, see component definition above */}
            </div>
          </div>
        </div>

        {/* Responsive: queues hidden on mobile (see drawer above), three-col on lg+ */}
        <div className="flex flex-wrap lg:flex-nowrap gap-4 lg:gap-6 items-start">

          {/* ── Left: Automation Queue ── */}
          <div id="tour-automation-queue" className="hidden lg:block lg:w-72 xl:w-80 flex-shrink-0 order-2 lg:order-1">
            <AutomationQueuePanel
              triggerOn={triggerOn}
              onToggle={() => setConfirmQueueToggle('automation')}
              toggleLoading={triggerLoading}
            />
          </div>

          {/* ── Center: Main content ── */}
          <div className="w-full lg:flex-1 min-w-0 order-1 lg:order-2">

            {/* ── Greeting ── */}
            <div className="mb-8">
              <h1 className="text-[28px] font-semibold leading-snug text-ink" style={{ fontFamily: 'Instrument Serif, serif' }}>
                {greeting}{firstName && (
                  <>, <em style={{ fontStyle: 'italic', color: 'var(--g)' }}>{firstName}.</em></>
                )}
              </h1>
              <p className="text-[15px] font-medium text-muted mt-1">Let's get you in the right rooms.</p>
            </div>

            {/* ── Profile setup prompt (only when profile is missing) ── */}
            {!hasProfile && (
              <div className="mb-5 flex items-center justify-between gap-3 rounded-card border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="h-7 w-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <p className="text-sm text-amber-800">Set up your outreach profile to start generating emails.</p>
                </div>
                <button onClick={() => setProfileOpen(true)} className="flex-shrink-0 text-xs font-semibold text-amber-800 underline hover:text-amber-900">
                  Complete Setup →
                </button>
              </div>
            )}

            {/* ── Stats ── */}
            <div id="tour-stats" className="grid grid-cols-3 gap-2.5 sm:gap-4 mb-6">
              {[
                { label: 'Applications', value: applications.length, sub: null },
                { label: 'Outreaches', value: totalOutreaches, sub: totalOutreaches > 0 ? 'emails drafted' : null },
                { label: 'Replies', value: totalReplies, sub: totalReplies === 0 ? 'none yet' : `${totalReplies} received` },
              ].map(({ label, value, sub }) => (
                <div key={label} className="bg-card border border-border rounded-card p-3 sm:p-5">
                  <p
                    className="text-2xl sm:text-[36px] leading-none text-ink font-bold"
                    style={{ fontFamily: 'Lora, serif' }}
                  >
                    {value}
                  </p>
                  <p className="text-xs sm:text-sm font-semibold text-muted mt-1.5 sm:mt-2 truncate">{label}</p>
                  {sub && (
                    <p className="text-[11px] sm:text-xs font-medium mt-1 truncate" style={{ color: label === 'Replies' && totalReplies === 0 ? 'var(--hint)' : 'var(--g)' }}>
                      {sub}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* ── Manual trigger form ── */}
            <div id="tour-manual-outreach" className="bg-card border border-border rounded-card p-4 sm:p-6 mb-6">
              <p className="text-[11px] uppercase tracking-[1.2px] text-hint font-semibold mb-4">Manual Outreach</p>

              {webhookState === 'loading' || (webhookState === 'success' && !outreachLaunched) ? (
                <div className="min-h-[340px] flex flex-col items-center justify-center py-8 px-4 text-center">
                  <SendLoader
                    done={webhookState === 'success'}
                    size={168}
                    minDurationMs={2600}
                    onLaunched={() => setOutreachLaunched(true)}
                    colors={{
                      green: 'var(--g, #10B981)',
                      ink: 'var(--ink, #1C1F1A)',
                      muted: 'var(--muted, #6b7280)',
                    }}
                  />
                </div>
              ) : webhookState === 'success' && outreachLaunched ? (
                <div className="min-h-[340px] flex flex-col items-center justify-center py-8 gap-3 text-center">
                  <div className="h-14 w-14 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-1">
                    <svg className="h-7 w-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-lg text-ink">Workflow triggered!</p>
                    <p className="text-sm text-muted max-w-sm mt-1 leading-relaxed">
                      {formSendMode === 'send'
                        ? 'Emails are sending gradually over the next few minutes to keep your Gmail account safe.'
                        : 'Drafts will appear in your Gmail in 2–3 minutes.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      resetWebhook();
                      setOutreachLaunched(false);
                      setForm({ company: '', role: '', country: '', max_outreach: 10 });
                      setFormSendMode('draft');
                      setShowReconnectGmail(false);
                    }}
                    className="text-sm font-semibold text-g hover:underline mt-2 inline-flex items-center gap-1"
                  >
                    Trigger another →
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {showReconnectGmail && (
                    <div className="mb-4 flex items-center justify-between gap-3 rounded-input border border-red-200 bg-red-50 px-4 py-3">
                      <p className="text-sm text-red-700">Gmail is not connected. Reconnect to trigger outreach.</p>
                      <a href="/settings" className="flex-shrink-0 text-xs font-medium text-red-700 underline hover:text-red-900">
                        Reconnect Gmail →
                      </a>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    {[
                      { key: 'company', label: 'Company', ph: 'Google' },
                      { key: 'role', label: 'Role', ph: 'Software Engineer' },
                    ].map(({ key, label, ph }) => (
                      <div key={key}>
                        <label className="block text-[11px] uppercase tracking-[1.2px] text-hint font-semibold mb-1.5">
                          {label}
                        </label>
                        <input
                          type="text"
                          placeholder={ph}
                          value={form[key as 'company' | 'role']}
                          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                          className="w-full rounded-input px-3 py-2.5 text-sm text-ink bg-card focus:outline-none transition-colors placeholder:text-hint"
                          style={{ border: '1px solid var(--border)' }}
                          onFocus={e => (e.target.style.borderColor = 'var(--g)')}
                          onBlur={e => (e.target.style.borderColor = 'var(--border)')}
                        />
                      </div>
                    ))}
                    <div className="col-span-1 sm:col-span-2">
                      <label className="block text-[11px] uppercase tracking-[1.2px] text-hint font-semibold mb-1.5">
                        Country <span className="normal-case text-hint font-normal">(optional — narrows recruiter search)</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Australia"
                        value={form.country}
                        onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                        className="w-full rounded-input px-3 py-2.5 text-sm text-ink bg-card focus:outline-none transition-colors placeholder:text-hint"
                        style={{ border: '1px solid var(--border)' }}
                        onFocus={e => (e.target.style.borderColor = 'var(--g)')}
                        onBlur={e => (e.target.style.borderColor = 'var(--border)')}
                      />
                    </div>
                  </div>

                  {/* Email count + mode selector */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-[11px] uppercase tracking-[1.2px] text-hint font-semibold">
                        Recruiters to contact
                      </label>
                      {/* Draft vs Send Now toggle */}
                      <div className="flex rounded-input overflow-hidden border border-border text-xs font-semibold">
                        <button
                          type="button"
                          onClick={() => setFormSendMode('draft')}
                          className="px-2.5 py-1 transition-colors"
                          style={{
                            backgroundColor: formSendMode === 'draft' ? 'var(--g)' : 'transparent',
                            color: formSendMode === 'draft' ? 'white' : 'var(--hint)',
                          }}
                        >
                          Draft
                        </button>
                        {canDirectSend ? (
                          <button
                            type="button"
                            onClick={() => setFormSendMode('send')}
                            className="px-2.5 py-1 transition-colors border-l border-border"
                            style={{
                              backgroundColor: formSendMode === 'send' ? 'var(--g)' : 'transparent',
                              color: formSendMode === 'send' ? 'white' : 'var(--hint)',
                            }}
                          >
                            Send Now
                          </button>
                        ) : (
                          <a
                            href="/pricing"
                            className="px-2.5 py-1 border-l border-border flex items-center gap-1"
                            style={{ color: 'var(--hint)' }}
                            title="Upgrade to Growth to unlock direct send"
                          >
                            🔒 Send Now
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {([3, 5, 10] as const).map(n => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setForm(f => ({ ...f, max_outreach: n }))}
                          className="flex-1 py-2 rounded-input text-sm font-semibold transition-colors border"
                          style={{
                            backgroundColor: form.max_outreach === n ? 'var(--g)' : 'var(--bg)',
                            color: form.max_outreach === n ? 'white' : 'var(--ink)',
                            borderColor: form.max_outreach === n ? 'var(--g)' : 'var(--border)',
                          }}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-hint mt-1.5">
                      Up to {form.max_outreach} — smaller companies may have fewer recruiters to find.
                    </p>
                  </div>

                  {/* Resume attachment toggle — Growth/Pro only */}
                  <div className="flex items-center justify-between py-1 px-1">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-ink">Attach Resume</span>
                      {!user?.resume_pdf_path && (
                        <span className="text-xs text-hint">
                          <a href="/settings" className="underline hover:text-ink">Upload in Settings</a> first
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={attachResume}
                      disabled={!user?.resume_pdf_path}
                      onClick={() => setAttachResume(v => !v)}
                      className="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ backgroundColor: attachResume && user?.resume_pdf_path ? 'var(--g)' : 'var(--border)' }}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${attachResume && user?.resume_pdf_path ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={!hasProfile}
                    title={!hasProfile ? 'Complete your profile first' : undefined}
                    className="w-full flex items-center justify-between gap-2 py-3 px-4 rounded-input text-sm font-semibold text-white disabled:opacity-60 transition-colors"
                    style={{ backgroundColor: 'var(--g)' }}
                    onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = 'var(--g-text)'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'var(--g)'; }}
                  >
                    {formSendMode === 'send' ? (
                      <>
                        <span>Find & Send {form.max_outreach} Emails</span>
                        <span className="text-xs font-normal" style={{ color: 'rgba(255,255,255,0.55)' }}>
                          1 credit / email
                        </span>
                      </>
                    ) : (
                      <>
                        <span>Find {form.max_outreach} Recruiters & Draft Emails</span>
                        <span className="text-xs font-normal" style={{ color: 'rgba(255,255,255,0.55)' }}>
                          1 credit / email sent
                        </span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* ── Applications list ── */}
            <div id="tour-applications" className="bg-card border border-border rounded-card overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <p className="text-[11px] uppercase tracking-[1.2px] text-hint font-semibold">Applications</p>
                  <span className="inline-flex items-center justify-center h-5 px-2 rounded-full bg-bg border border-border text-xs text-muted font-medium">
                    {applications.length}
                  </span>
                  <button
                    onClick={() => refetchPipeline()}
                    className="text-xs text-g hover:underline ml-1"
                  >
                    ↻ Refresh
                  </button>
                </div>
                {/* Pill search */}
                <input
                  type="text"
                  placeholder="Search by company or role…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="bg-bg border border-border px-4 py-1.5 text-sm text-ink focus:outline-none transition-colors w-full sm:w-56 placeholder:text-hint"
                  style={{ borderRadius: '20px' }}
                  onFocus={e => (e.target.style.borderColor = 'var(--g)')}
                  onBlur={e => (e.target.style.borderColor = 'var(--border)')}
                />
              </div>

              {pipelineLoading ? (
                <div className="flex justify-center py-12"><Spinner /></div>
              ) : filteredApps.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-6">
                  <div className="h-12 w-12 rounded-full bg-bg flex items-center justify-center mb-3">
                    <svg className="h-6 w-6 text-hint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="font-medium text-ink">No applications yet</p>
                  <p className="text-sm text-muted mt-1">Trigger your first outreach above to get started</p>
                </div>
              ) : (
                <>
                  <div>
                    {paginatedApps.map(app => <ApplicationRow key={app.id} app={app} />)}
                  </div>
                  {filteredApps.length > 0 && (
                    <div className="px-5 py-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 bg-white">
                      <p className="text-xs text-muted">
                        Showing{' '}
                        <span className="font-medium text-ink">
                          {(safeCurrentPage - 1) * APPLICATIONS_PER_PAGE + 1}
                        </span>
                        –
                        <span className="font-medium text-ink">
                          {Math.min(safeCurrentPage * APPLICATIONS_PER_PAGE, filteredApps.length)}
                        </span>{' '}
                        of{' '}
                        <span className="font-medium text-ink">{filteredApps.length}</span>{' '}
                        applications
                      </p>
                      <div className="overflow-x-auto max-w-full py-0.5">
                        <Pagination
                          currentPage={safeCurrentPage}
                          totalPages={totalPages}
                          onPageChange={page => setCurrentPage(page)}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
          </div>{/* end center column */}

          {/* ── Right: Follow-up Queue (Referral Queue disabled on staging, see component definition above) ── */}
          <div id="tour-followup-queue" className="hidden lg:flex lg:w-72 xl:w-80 flex-shrink-0 order-3 flex-col gap-4">
            <FollowUpQueuePanel
              followUpOn={followUpOn}
              onToggle={() => setConfirmQueueToggle('followup')}
              canFollowUp={user?.plan === 'growth' || user?.plan === 'pro'}
              onPreview={setReviewFollowUpId}
            />
            {/* <ReferralQueuePanel /> */}
          </div>

        </div>{/* end flex row */}
      </DashboardLayout>

      <ProfileSlideOver
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onSaved={() => { }}
        defaultRole={form.role}
        defaultCompany={form.company}
        userName={user?.name}
      />

      {pendingDirectSend && (
        <ConfirmSendModal
          company={form.company}
          role={form.role}
          maxOutreach={form.max_outreach}
          onCancel={() => setPendingDirectSend(false)}
          onConfirm={handleConfirmDirectSend}
        />
      )}

      {confirmQueueToggle && (
        <QueueToggleModal
          type={confirmQueueToggle}
          currentState={confirmQueueToggle === 'automation' ? triggerOn : followUpOn}
          onCancel={() => setConfirmQueueToggle(null)}
          onConfirm={handleConfirmQueueToggle}
          loading={confirmQueueToggle === 'automation' ? triggerLoading : false}
        />
      )}

      <FollowUpPreviewModal
        open={Boolean(reviewFollowUpId)}
        outreachId={reviewFollowUpId}
        onClose={() => setReviewFollowUpId(null)}
        onError={(msg) => setToast({ message: msg, type: 'error' })}
        onSent={(_id, recruiterName, company) => {
          queryClient.invalidateQueries({ queryKey: ['follow-ups-pending'] });
          setToast({ message: `Follow-up sent to ${recruiterName} at ${company}`, type: 'success' });
        }}
        onSkipped={(_id, recruiterName, company) => {
          queryClient.invalidateQueries({ queryKey: ['follow-ups-pending'] });
          setToast({ message: `Follow-up skipped for ${recruiterName} at ${company}`, type: 'info' });
        }}
      />
    </>
  );
}
